import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MoneyInput } from '../MoneyInput';
import { useMemo } from 'react';
import CategorySelector from '../CategorySelector';
import type { TransactionFormProps, CategorySplitWithData } from './types';
import type { CategoryWithMonthData } from '../../types/category';

export default function TransactionRow({
  transaction,
  index,
  mode,
  isOnlyCard,
  accounts,
  allMonthPlans,
  budgetMonthCategories,
  loadingBudgetCategories,
  formatMoney,
  formatMonth,
  onUpdate,
  onRemove,
  onBudgetMonthChange,
  onCreateRowsForCategories,
}: TransactionFormProps) {
  const { t } = useTranslation();

  const { type, amount, categorySplits, selectedAccount, fromAccount, toAccount, description, date, selectedBudgetMonthPlan, hasError, errorMessage } = transaction;

  // Flatten categories to get all subcategories with parent names
  const allSubcategories = useMemo(() =>
    budgetMonthCategories.flatMap(group =>
      (group.children || []).map(cat => ({
        ...cat,
        parentName: group.name
      }))
    ), [budgetMonthCategories]);

  // Helper function to get pay full amount for a category
  const getCategoryPayFullData = (cat: CategoryWithMonthData) => {
    if (cat.is_goal) {
      if (cat.goal_monthly_target) {
        const remaining = Math.max(0, cat.goal_monthly_target - (cat.transaction || 0));
        return { amount: remaining, label: t('transaction.remaining') };
      } else {
        const remaining = Math.max(0, (cat.goal_target || 0) - (cat.goal_saved || 0));
        return { amount: remaining, label: t('transaction.remaining') };
      }
    }
    // Non-goal: use balance (ensure non-negative for overspent categories)
    return { amount: Math.max(0, cat.balance || 0), label: t('transaction.remaining') };
  };

  // Handle type change
  const handleTypeChange = (newType: 'expense' | 'income' | 'transfer') => {
    if (newType === type) return;

    const updates: Partial<typeof transaction> = { type: newType, hasError: false, errorMessage: undefined };

    if (newType !== 'expense') {
      updates.categorySplits = [];
    }
    if (newType === 'transfer') {
      updates.selectedAccount = '';
    } else {
      updates.fromAccount = '';
      updates.toAccount = '';
    }

    onUpdate(index, updates);
  };

  // Handle category toggle - desktop: if selecting results in multiple categories, create separate rows
  const handleToggleCategory = (categoryId: string) => {
    if (categorySplits.some(s => s.category_id === categoryId)) {
      // Deselect - remove this specific category
      const newSplits = categorySplits.filter(s => s.category_id !== categoryId);
      onUpdate(index, { categorySplits: newSplits });
    } else {
      // Check if we already have categories selected - if so, create multiple rows
      if (categorySplits.length > 0 && onCreateRowsForCategories) {
        // Combine existing category IDs with new one and create rows
        const allCategoryIds = [...categorySplits.map(s => s.category_id), categoryId];
        onCreateRowsForCategories(transaction, allCategoryIds);
      } else {
        // First category selection - just add to current row
        const categoryData = allSubcategories.find(c => c.id === categoryId);
        if (categoryData) {
          // Preserve existing amount from previous category or base amount
          const existingAmount = categorySplits.length > 0
            ? categorySplits[0]?.amount || 0
            : amount;
          const payFullData = getCategoryPayFullData(categoryData);
          const newSplit: CategorySplitWithData = {
            category_id: categoryId,
            amount: existingAmount,
            categoryName: categoryData.name,
            parentName: categoryData.parentName,
            remainingAllocation: payFullData.amount,
            isGoal: categoryData.is_goal,
            goalTargetMonth: categoryData.goal_target_month,
            displayLabel: payFullData.label
          };
          onUpdate(index, { categorySplits: [newSplit], hasError: false, errorMessage: undefined });
        }
      }
    }
  };

  // Handle batch toggle - for desktop: create multiple rows for each category
  const handleBatchToggleCategories = (categoryIds: string[], shouldSelect: boolean) => {
    if (shouldSelect && categoryIds.length > 0) {
      // If multiple categories selected and callback exists, create separate rows
      if (categoryIds.length > 1 && onCreateRowsForCategories) {
        onCreateRowsForCategories(transaction, categoryIds);
        return;
      }

      // Single category - update current row
      const categoryId = categoryIds[0];
      const categoryData = allSubcategories.find(c => c.id === categoryId);
      if (categoryData) {
        // Preserve existing amount from previous category or base amount
        const existingAmount = categorySplits.length > 0
          ? categorySplits[0]?.amount || 0
          : amount;
        const payFullData = getCategoryPayFullData(categoryData);
        const newSplit: CategorySplitWithData = {
          category_id: categoryId,
          amount: existingAmount,
          categoryName: categoryData.name,
          parentName: categoryData.parentName,
          remainingAllocation: payFullData.amount,
          isGoal: categoryData.is_goal,
          goalTargetMonth: categoryData.goal_target_month,
          displayLabel: payFullData.label
        };
        onUpdate(index, { categorySplits: [newSplit], hasError: false, errorMessage: undefined });
      }
    } else {
      onUpdate(index, { categorySplits: [] });
    }
  };

  // Handle category amount update
  const handleCategoryAmountChange = (newAmount: number) => {
    if (categorySplits.length > 0) {
      onUpdate(index, {
        categorySplits: categorySplits.map((s, i) => i === 0 ? { ...s, amount: newAmount } : s)
      });
    } else {
      onUpdate(index, { amount: newAmount });
    }
  };

  // Row background based on error state
  const getRowClass = () => {
    if (hasError) return 'bg-red-50';
    return 'bg-white hover:bg-[#f5f5dc]';
  };

  // Disabled field style
  const disabledFieldClass = "w-full px-2 py-2 bg-gray-100 border-2 border-gray-300 text-gray-400 outline-none font-mono text-xs cursor-not-allowed";
  const activeFieldClass = "w-full px-2 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-xs";

  return (
    <tr className={`${getRowClass()} border-b-2 border-[#077f5d]`}>
      {/* Row number */}
      <td className="px-1 py-3 text-center font-mono text-xs font-bold text-[#077f5d]" style={{ width: '40px', minWidth: '40px' }}>
        #{index + 1}
      </td>

      {/* Type */}
      <td className="px-1 py-3" style={{ width: '110px', minWidth: '110px' }}>
        <select
          value={type}
          onChange={(e) => handleTypeChange(e.target.value as 'expense' | 'income' | 'transfer')}
          className={`w-full px-2 py-2 border-2 border-[#077f5d] outline-none font-mono text-xs ${type === 'expense' ? 'bg-red-50 text-red-700' :
              type === 'income' ? 'bg-green-50 text-green-700' :
                'bg-blue-50 text-blue-700'
            }`}
          style={{ boxShadow: '2px 2px 0 #077f5d' }}
        >
          <option value="expense">{t('transaction.expense')}</option>
          <option value="income">{t('transaction.income')}</option>
          <option value="transfer">{t('transaction.transfer')}</option>
        </select>
      </td>

      {/* Source Account - Active for expense & transfer, Disabled for income */}
      <td className="px-1 py-3" style={{ width: '150px', minWidth: '150px' }}>
        {type === 'income' ? (
          <select disabled className={disabledFieldClass} style={{ boxShadow: 'none' }}>
            <option>-</option>
          </select>
        ) : (
          <select
            value={type === 'transfer' ? fromAccount : selectedAccount}
            onChange={(e) => type === 'transfer'
              ? onUpdate(index, { fromAccount: e.target.value, hasError: false })
              : onUpdate(index, { selectedAccount: e.target.value, hasError: false })
            }
            className={activeFieldClass}
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            <option value="">{t('transaction.source_fund')}</option>
            {accounts.filter(a => a.status === 'active').map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* To Account - Active for income & transfer, Disabled for expense */}
      <td className="px-1 py-3" style={{ width: '150px', minWidth: '150px' }}>
        {type === 'expense' ? (
          <select disabled className={disabledFieldClass} style={{ boxShadow: 'none' }}>
            <option>-</option>
          </select>
        ) : type === 'transfer' ? (
          <select
            value={toAccount}
            onChange={(e) => onUpdate(index, { toAccount: e.target.value, hasError: false })}
            className={activeFieldClass}
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            <option value="">{t('transaction.to_account')}</option>
            {accounts.filter(a => a.status === 'active' && a.id !== fromAccount).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={selectedAccount}
            onChange={(e) => onUpdate(index, { selectedAccount: e.target.value, hasError: false })}
            className={activeFieldClass}
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            <option value="">{t('transaction.source_fund')}</option>
            {accounts.filter(a => a.status === 'active').map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Budget Month - Active for expense & income, Active for transfer if category selected */}
      <td className="px-1 py-3" style={{ width: '120px', minWidth: '120px' }}>
        {type === 'transfer' && categorySplits.length === 0 ? (
          <select disabled className={disabledFieldClass} style={{ boxShadow: 'none' }}>
            <option>-</option>
          </select>
        ) : (
          <select
            value={selectedBudgetMonthPlan}
            onChange={(e) => onBudgetMonthChange(index, e.target.value)}
            className={activeFieldClass}
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            {allMonthPlans.map(mp => (
              <option key={mp.id} value={mp.id}>
                {formatMonth(mp.month)}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Category - Active for expense, Optional for transfer, Disabled for income */}
      <td className="px-1 py-3" style={{ width: '180px', minWidth: '180px' }}>
        {type === 'income' ? (
          <select disabled className={disabledFieldClass} style={{ boxShadow: 'none' }}>
            <option>-</option>
          </select>
        ) : (
          <CategorySelector
            categories={budgetMonthCategories}
            selectedCategoryIds={categorySplits.map(s => s.category_id)}
            onToggleCategory={handleToggleCategory}
            onBatchToggle={handleBatchToggleCategories}
            loading={loadingBudgetCategories}
            type={type}
            hideLabel={true}
            compact={true}
            remainingAllocation={categorySplits[0]?.remainingAllocation}
            formatMoney={formatMoney}
          />
        )}
      </td>

      {/* Amount */}
      <td className="px-1 py-3" style={{ width: '110px', minWidth: '110px' }}>
        <div>
          <MoneyInput
            value={(type === 'expense' || type === 'transfer') && categorySplits.length > 0 ? (categorySplits[0]?.amount || 0) : amount}
            onValueChange={handleCategoryAmountChange}
            placeholder="0"
            className={activeFieldClass}
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
            allowNegative={false}
            useCustomKeyboard={true}
            keyboardTitle={t('transaction.amount')}
          />
          {(type === 'expense' || type === 'transfer') && categorySplits.length > 0 && categorySplits[0]?.remainingAllocation ? (
            <button
              type="button"
              onClick={() => handleCategoryAmountChange(categorySplits[0].remainingAllocation || 0)}
              className="mt-1 w-full py-1 bg-[#3cbe8c] border border-[#077f5d] text-white text-[9px] font-mono hover:bg-[#2da877]"
            >
              {t('transaction.pay_full')} ({formatMoney(categorySplits[0].remainingAllocation)})
            </button>
          ) : null}
        </div>
      </td>

      {/* Description */}
      <td className="px-1 py-3" style={{ width: '150px', minWidth: '150px' }}>
        <input
          type="text"
          value={description}
          onChange={(e) => onUpdate(index, { description: e.target.value })}
          placeholder={t('transaction.description_placeholder')}
          className={activeFieldClass}
          style={{ boxShadow: '2px 2px 0 #077f5d' }}
        />
      </td>

      {/* Date */}
      <td className="px-1 py-3" style={{ width: '120px', minWidth: '120px' }}>
        <input
          type="date"
          value={date || new Date().toISOString().split('T')[0]}
          onChange={(e) => onUpdate(index, { date: e.target.value || new Date().toISOString().split('T')[0] })}
          max={new Date().toISOString().split('T')[0]}
          className={activeFieldClass}
          style={{ boxShadow: '2px 2px 0 #077f5d' }}
          required
        />
      </td>

      {/* Delete button */}
      <td className="px-1 py-3 text-center" style={{ width: '44px', minWidth: '44px' }}>
        {mode === 'add' && !isOnlyCard ? (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-2 bg-red-500 border-2 border-red-700 hover:bg-red-600"
            style={{ boxShadow: '2px 2px 0 #991b1b' }}
            title={t('transaction.multi.remove')}
          >
            <X className="w-4 h-4 text-white" />
          </button>
        ) : null}
      </td>

      {/* Error row */}
      {hasError && errorMessage && (
        <td colSpan={10} className="px-4 py-2 bg-red-100">
          <p className="text-xs text-red-600 font-mono">{errorMessage}</p>
        </td>
      )}
    </tr>
  );
}
